import React, { createContext, useContext, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import { useChat } from "./ChatContext";
// NOTE: toast import removed — notifications are system-level only (via Service Worker)
import { useSystemNotifications } from "../hooks/useSystemNotifications";
import { usePushSubscription } from "../hooks/usePushSubscription";

const SocketContext = createContext();
const NotifPermissionContext = createContext({ permissionState: "default", requestPermission: async () => {} });

export const useSocket = () => useContext(SocketContext);
export const useNotifPermission = () => useContext(NotifPermissionContext);

export const SocketProvider = ({ children }) => {
    const { user } = useAuth();
    const { setUnreadCounts, setTotalUnread, setDirectMessages, refreshData, setOnlineUsers, activeChat } = useChat();
    const socket = useRef(null);
    // Keep stable refs so socket listeners always read the latest values
    const refreshDataRef = useRef(refreshData);
    const activeChatRef  = useRef(activeChat);
    useEffect(() => { refreshDataRef.current = refreshData; }, [refreshData]);
    useEffect(() => { activeChatRef.current  = activeChat;  }, [activeChat]);

    // ── System notifications (Web Notifications API) ──────────────────────────
    const pushHook = usePushSubscription();
    const userId = user?._id || user?.id || null;
    const { showNotification, permissionState, requestPermission } = useSystemNotifications(activeChatRef, pushHook, userId);

    useEffect(() => {
        const userId = user?._id || user?.id;
        if (!userId) return;

        // Extract host for socket.io
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:1338/api/";
        const socketUrl = backendUrl.replace("/api/", "/").replace("/api", "");

        socket.current = io(socketUrl, {
            transports: ["websocket"],
            reconnectionAttempts: 5,
            timeout: 20000,
        });

        socket.current.on("connect", () => {
            console.log("Socket connected:", socket.current.id);
            socket.current.emit("register", userId);
            // Re-verify push subscription on every connect (handles reconnects).
            // subscribeToPush is idempotent — safe to call on initial connect and reconnects.
            if (pushHook?.subscribeToPush && userId) {
                pushHook.subscribeToPush(userId);
            }
        });

        socket.current.on("connect_error", (err) => {
            console.error("Socket connection error:", err.message);
        });

        socket.current.on("reconnect_attempt", (attempt) => {
            //============= [NOTIF DEBUG] Socket reconnect attempt
            console.log("[NOTIF][SOCKET] Reconnect attempt #", attempt, "— server may be down or unreachable");
            //=============
        });

        socket.current.on("reconnect_failed", () => {
            //============= [NOTIF DEBUG] Socket reconnect failed — all attempts exhausted
            console.error("[NOTIF][SOCKET] Reconnect FAILED — socket events (including notifications) will not work until server is reachable");
            //=============
        });

        // Listen for data updates (unread counts and live status)
        socket.current.on("dataUpdate", (res) => {
            if (res?.userId === userId) {
                refreshDataRef.current();
            }
        });

        // Listen for data updates in channels
        socket.current.on("dataUpdate-in-channel", (res) => {
            if (res?.userId === userId) {
                const data = res?.data || {};
                setUnreadCounts(prev => ({ ...prev, ...data }));
            }
        });

        // Sync unread counts for specific channels
        socket.current.on("channelUnreadUpdate", (data) => {
            if (data?.channelId) {
                setUnreadCounts(prev => ({ ...prev, [data.channelId]: data.unreadCount }));
            }
        });

        // Listen for real-time channel membership updates
        socket.current.on("channel:member_added", (data) => {
            if (data?.memberId?.toString() === userId.toString()) {
                console.log(`[SocketContext] 📥 You were added to a channel (${data.channelId}), refreshing data...`);
                refreshDataRef.current();
            }
        });

        socket.current.on("channel:member_removed", (data) => {
            if (data?.memberId?.toString() === userId.toString()) {
                console.log(`[SocketContext] 📤 You were removed from a channel (${data.channelId}), refreshing data...`);
                refreshDataRef.current();
            }
        });

        // Listen for real-time channel creation
        socket.current.on("channel:created", (channel) => {
            if (!channel || !channel.members) return;
            const isMember = channel.members.some(mId =>
                (typeof mId === 'string' ? mId : (mId._id || mId.id)) === userId
            );
            if (isMember) {
                console.log(`[SocketContext] 📢 New channel created (#${channel.name}), refreshing data...`);
                refreshDataRef.current();
            }
        });

        // Real-time presence: track who is online
        socket.current.on("presenceUpdate", (onlineUserIds) => {
            setOnlineUsers(onlineUserIds || []);
        });

        // ── Real-time notifications ───────────────────────────────────────────
        // Fires when someone sends a DM or channel message to this user.
        // If the user is already viewing that chat, suppress the toast but
        // still increment the unread count (it will be reset by markChatAsRead).
        socket.current.on("notification:receive", (notif) => {
            const { chatType, chatId, senderName, messagePreview, channelName } = notif;

            //============= [NOTIF DEBUG] notification:receive arrived on client
            console.log("[NOTIF][CLIENT] notification:receive received:", {
                chatType,
                chatId,
                senderName,
                channelName,
                messagePreview: (messagePreview || "").slice(0, 40),
                messageId: notif?.messageId,
            });
            //=============

            if (!chatId) {
                //============= [NOTIF DEBUG] Dropped — chatId is missing
                console.warn("[NOTIF][CLIENT] notification:receive DROPPED — chatId is missing");
                //=============
                return;
            }

            const currentChat = activeChatRef.current;
            const isViewingThisChat =
                currentChat?.type === (chatType === "direct" ? "dm" : "channel") &&
                (currentChat?.data?._id || currentChat?.data?.id)?.toString() === chatId.toString();

            //============= [NOTIF DEBUG] Active-chat suppression check
            console.log("[NOTIF][CLIENT] isViewingThisChat:", isViewingThisChat, "| activeChat:", {
                type: currentChat?.type,
                id: currentChat?.data?._id || currentChat?.data?.id,
            });
            //=============

            // Always increment unread count unless user is actively viewing the chat
            if (!isViewingThisChat) {
                setUnreadCounts(prev => {
                    const current = prev[chatId] || 0;
                    const next = { ...prev, [chatId]: current + 1 };
                    const newTotal = Object.values(next).reduce((sum, v) => sum + (v || 0), 0);
                    setTotalUnread(newTotal);
                    return next;
                });
            }

            // ── System notification (Web Notifications API) ───────────────────
            // The hook handles its own suppression + dedup guards internally.

            //============= [NOTIF DEBUG] Before calling showNotification (system notification)
            console.log("[NOTIF][CLIENT] Calling showNotification — permission:", typeof Notification !== "undefined" ? Notification.permission : "API unavailable");
            //=============

            showNotification(notif);

            //============= [NOTIF DEBUG] After calling showNotification
            console.log("[NOTIF][CLIENT] showNotification called ✅");
            //=============
        });

        return () => {
            if (socket.current) {
                socket.current.disconnect();
            }
        };
    // ⚠️ refreshData intentionally excluded — we use refreshDataRef above.
    // Including it would reconnect the socket every time refreshData changes.
    }, [user?._id, user?.id, setUnreadCounts, setTotalUnread, setDirectMessages, setOnlineUsers]);

    return (
        <SocketContext.Provider value={socket.current}>
            <NotifPermissionContext.Provider value={{ permissionState, requestPermission }}>
                {children}
            </NotifPermissionContext.Provider>
        </SocketContext.Provider>
    );
};